
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import { cleanReturnTo } from '../auth/return-to';
import { clearStop, mayHandOver, readStop, rememberStop, stopOf, type SignInStop } from '../auth/sign-in-stop';
import { LegalFooter } from '../features/legal/LegalFooter';
import { userManager } from '../auth/oidc';
import { choosable, languageEntry, useI18n, type Language } from '../i18n';
import type { MessageKey } from '@smbsuite/i18n';
import { Icon, SelectField, Spinner } from '../ui';

// The code from Keycloak is exchanged exactly once, even when React runs the effect twice.
let callback: Promise<string> | null = null;

/** `isPage`: whether the app has a page for a path, so a return address with none becomes the start page. */
export function AuthCallback({ isPage }: { isPage?: (path: string) => boolean }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  useEffect(() => {
    callback ??= userManager
      .signinRedirectCallback()
      .then((user) => {
        // Signed in: whatever stopped an earlier attempt is over.
        clearStop();
        return cleanReturnTo((user.state as { returnTo?: string } | undefined)?.returnTo, isPage);
      })
      // Cancelled, expired or refused (TF-239): the start page says which, instead of going round again.
      .catch((error: unknown) => {
        rememberStop(stopOf(error));
        return '/';
      });
    void callback.then((to) => navigate(to, { replace: true }));
  }, [navigate, isPage]);
  return (
    <div className="center-page">
      <Spinner label={t('auth.signingIn')} />
    </div>
  );
}

/**
 * An address people type to sign in, /login and the like (TF-235), and the website's Sign in (TF-239):
 * signed out, it goes straight to the sign-in, which comes back to the start page or to `?return=` (a
 * page inside the app); signed in, it is that page. `?lang=` shows the sign-in, and then the app, in
 * the website's language; `?email=` fills in the e-mail.
 */
export function SignInAddress() {
  const { me, ready, signIn } = useAuth();
  const { t, setLanguage } = useI18n();
  const [params] = useSearchParams();
  const started = useRef(false);
  const returnTo = cleanReturnTo(params.get('return'));
  useEffect(() => {
    if (!ready || me || started.current) return;
    started.current = true;
    // Asked for in so many words: whatever stopped an earlier sign-in no longer applies.
    clearStop();
    const language = params.get('lang')?.toLowerCase() ?? null;
    const offered = language ? choosable().some((entry) => entry.code === language) : false;
    if (offered) setLanguage(language as Language);
    void signIn(returnTo, { language: offered ? language : null, email: params.get('email') });
  }, [ready, me, signIn, params, returnTo, setLanguage]);
  if (me) return <Navigate to={returnTo} replace />;
  return (
    <div className="center-page">
      <Spinner label={t('auth.signingIn')} />
    </div>
  );
}

/** /logout (TF-235): signs out at Keycloak, which comes back to the signed-out start page. */
export function SignOutAddress() {
  const { me, ready, signOut } = useAuth();
  const { t } = useI18n();
  const started = useRef(false);
  useEffect(() => {
    if (!ready || !me || started.current) return;
    started.current = true;
    void signOut();
  }, [ready, me, signOut]);
  if (ready && !me) return <Navigate to="/" replace />;
  return (
    <div className="center-page">
      <Spinner label={t('auth.signingOut')} />
    </div>
  );
}

/**
 * A page behind sign-in. Without a session it hands over to Keycloak at once and comes back to the
 * same page (TF-239); the welcome page is only for a person who signed out or whose sign-in failed.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { me, ready, signIn } = useAuth();
  const { t } = useI18n();
  const [stop, setStop] = useState<SignInStop | null>(readStop);
  const handingOver = ready && !me && !stop;
  useEffect(() => {
    if (!handingOver) return;
    if (!mayHandOver()) {
      // Went round and came back without a session more than once: ask instead of looping.
      rememberStop('failed');
      setStop('failed');
      return;
    }
    void signIn().catch(() => setStop('failed'));
  }, [handingOver, signIn]);
  if (!ready || handingOver) {
    return (
      <div className="center-page">
        <Spinner label={t(ready ? 'auth.signingIn' : 'common.loading')} />
      </div>
    );
  }
  return me ? <>{children}</> : <SignInPage stop={stop} />;
}

export function LanguagePicker({ id = 'language' }: { id?: string }) {
  const { language, entry, setLanguage, t } = useI18n();
  return (
    <SelectField
      id={id}
      label={t('nav.language')}
      hideLabel
      dense
      className="m-0"
      value={language}
      onChange={(value) => setLanguage(value as Language)}
      options={choosable().map((option) => ({ value: option.code, label: option.name }))}
      hint={entry.backend !== entry.code ? t('nav.languageBackend', { language: languageEntry(entry.backend).name }) : undefined}
    />
  );
}

const STOP_TEXT: Record<SignInStop, MessageKey> = {
  signedOut: 'auth.signedOut',
  cancelled: 'auth.signInCancelled',
  expired: 'auth.signInExpired',
  failed: 'auth.signInFailed',
};

/** After signing out, or a sign-in that did not finish: says which, and starts over on request. */
function SignInPage({ stop }: { stop: SignInStop | null }) {
  const { signIn } = useAuth();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  return (
    <main className="center-page">
      <div className="center-card">
        <div className="brand">
          <img src="/favicon.svg" alt="" />
          <span>{t('app.name')}</span>
        </div>
        <h1>{t('auth.welcome')}</h1>
        {stop && (
          <p role={stop === 'signedOut' ? 'status' : 'alert'} className={stop === 'signedOut' ? undefined : 'form-error'}>
            {t(STOP_TEXT[stop])}
          </p>
        )}
        <p className="muted">{t('app.tagline')}</p>
        <p className="muted">{t('auth.signInHint')}</p>
        <div className="btn-row mt-6 justify-between">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              clearStop();
              void signIn().catch(() => setBusy(false));
            }}
          >
            <Icon name="login" size={18} />
            {t('auth.signIn')}
          </button>
          <LanguagePicker id="signin-language" />
        </div>
      </div>
      <LegalFooter />
    </main>
  );
}
